import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import type { Variants } from 'framer-motion';
import type { ReactNode } from 'react';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { Box } from '@mui/material';
import MuiButton from '@mui/material/Button';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Colors } from '../../styles';

export type VariantType = 'success' | 'addButton' | 'notification' | 'cancel' | 'error' | 'warning';

interface BasicButtonProps extends Omit<MuiButtonProps, 'variant'> {
  children: ReactNode;
  variantType?: VariantType;
  whiteTheme?: boolean;
  allowBreakLine?: boolean;
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  notificationCount?: number;
  animate?: boolean;
}

const jingleVariants: Variants = {
  idle: { rotate: 0, scale: 1 },
  jingle: {
    rotate: [0, -10, 8, -6, 4, -2, 0],
    scale: [1, 1.04, 1, 1.02, 1, 1, 1],
    transition: { duration: 0.8, ease: 'easeInOut' },
  },
};

function getButtonStyles(variantType?: VariantType, whiteTheme?: boolean, size?: 'small' | 'medium' | 'large', allowBreakLine?: boolean, fullWidth?: boolean, disabled?: boolean) {
  return {
    'position': 'relative',
    'textTransform': 'none',
    'height': size === 'small' ? '32px' : size === 'medium' ? '42px' : '52px',
    'width': fullWidth
      ? '100%'
      : allowBreakLine
        ? 'auto'
        : size === 'small'
          ? '100px'
          : size === 'medium'
            ? '145px'
            : '160px',
    'fontSize': size === 'small' ? '13px' : size === 'medium' ? '15px' : '17px',
    'fontWeight': 600,
    'borderRadius': '8px',
    'whiteSpace': allowBreakLine ? 'normal' : 'nowrap',
    'minWidth': fullWidth || allowBreakLine ? 'auto' : 'fit-content',
    'textAlign': 'center',
    'overflow': 'visible',
    'transition': 'all 0.3s ease-in-out',
    'cursor': 'pointer',
    'boxShadow': whiteTheme
      ? '0px 4px 8px rgba(0, 0, 0, 0.1)'
      : '0px 4px 8px rgba(0, 0, 0, 0.2)',
    ...(variantType === 'notification' && { paddingLeft: '44px' }),
    'background': variantType === 'success'
      ? Colors.successGreen
      : variantType === 'addButton'
        ? 'linear-gradient(135deg, #c05a3a, #d4825e)'
        : variantType === 'notification'
          ? 'linear-gradient(135deg, #d4825e 0%, #f0d5c7 100%)'
          : variantType === 'cancel'
            ? Colors.grey
            : variantType === 'error'
              ? '#f44336'
              : variantType === 'warning'
                ? '#ff9800'
                : whiteTheme
                  ? '#FFFFFF'
                  : Colors.brandPrimary,
    'color': variantType === 'success'
      ? '#FFFFFF'
      : variantType === 'cancel'
        ? Colors.greyDarker
        : variantType === 'error'
          ? '#FFFFFF'
          : variantType === 'warning'
            ? '#FFFFFF'
            : whiteTheme
              ? Colors.black
              : '#FFFFFF',
    'border': whiteTheme && !disabled ? `2px solid ${Colors.brandPrimary}` : 'none',
    '&:hover': {
      background: variantType === 'success'
        ? '#6a9e6a'
        : variantType === 'addButton'
          ? 'linear-gradient(135deg, #a04a2e, #c05a3a)'
          : variantType === 'notification'
            ? 'linear-gradient(135deg, #c05a3a 0%, #d4825e 100%)'
            : variantType === 'cancel'
              ? Colors.greyDark
              : variantType === 'error'
                ? '#d32f2f'
                : variantType === 'warning'
                  ? '#f57c00'
                  : whiteTheme
                    ? Colors.greyLight
                    : '#a04a2e',
      boxShadow: whiteTheme
        ? '0px 6px 12px rgba(0, 0, 0, 0.15)'
        : '0px 6px 12px rgba(0, 0, 0, 0.3)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(1px)',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      background: '#e0e0e0',
      color: '#9e9e9e',
      boxShadow: 'none',
      cursor: 'not-allowed',
    },
  };
}

const Button: React.FC<BasicButtonProps> = ({
  children,
  variantType,
  whiteTheme = false,
  allowBreakLine = false,
  notificationCount,
  animate,
  disabled,
  size = 'medium',
  fullWidth,
  ...rest
}) => {
  const [doAnim, setDoAnim] = useState(false);
  useEffect(() => {
    if (!animate) {
      setDoAnim(false);
      return;
    }
    const t = setTimeout(() => setDoAnim(true), 1000);
    return () => clearTimeout(t);
  }, [animate]);

  return (
    <Box
      component={motion.div}
      initial="idle"
      animate={doAnim ? 'jingle' : 'idle'}
      variants={jingleVariants}
      onAnimationComplete={() => setDoAnim(false)}
      sx={{
        display: fullWidth ? 'block' : 'inline-block',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      <MuiButton
        {...rest}
        disabled={disabled}
        size={size}
        fullWidth={fullWidth}
        sx={getButtonStyles(variantType, whiteTheme, size, allowBreakLine, fullWidth, disabled)}
        aria-label={
          typeof notificationCount === 'number' && notificationCount > 0
            ? `${rest?.['aria-label'] ?? 'notifications'}: ${notificationCount}`
            : rest?.['aria-label']
        }
      >
        {variantType === 'notification'
          ? (
              <>
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <NotificationsNoneOutlinedIcon fontSize="small" />
                </Box>
                <span>{children}</span>
              </>
            )
          : (
              <>{children}</>
            )}

        {typeof notificationCount === 'number' && notificationCount > 0 && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: '#ef5350',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
              zIndex: 2,
              border: '2px solid #ffffff',
            }}
          >
            {notificationCount > 99 ? '99+' : notificationCount}
          </Box>
        )}
      </MuiButton>
    </Box>
  );
};

export default Button;
